import { useState, useEffect } from 'react';
import { Menu, Modal } from 'antd';
import { useHistory } from 'react-router-dom';

import Icon, {
  IdcardOutlined,
  UserOutlined,
  StarOutlined,
  EditTwoTone,
  HistoryOutlined,
  SolutionOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
  DollarOutlined,
  MedicineBoxOutlined,
  FileTextOutlined,
} from '@ant-design/icons';

import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { selectPermissions } from 'features/auth/authSlice';
import { selectInsurance } from 'features/insurances/insuranceSlice';
import { getSchedulePremium, getScheduleDecrease } from 'features/insurances/insuranceAction';
import { PERMISSION_PERSONNEL_RECORD_APPROVAL } from 'constants/permissions';
import { notificationToast } from 'components/notificationToast';
import { Notification } from 'constants/notification';

import AvatarBig from 'components/AvatarBig';
import EmployeeUpdateId from 'features/employee/pages/EmployeeUpdateId';
import Note from 'components/Note';
import { StyledEmployeeAddModalAvatar, EmployeeAvatar } from './styled';

interface SideBarMenuProps {
  isShowFullMenu: boolean;
  isShowHistory?: boolean;
  isShowContract?: boolean;
  employeeData: any;
  openKeys: string;
  employeeMenuKey: string;
  timesheetMenuKey?: string;
  performanceMenuKey?: string;
  personalMenuKey: string;
  skillMenuKey: string;
  historyMenuKey: string;
  contractMenuKey?: string;
  allowanceMenuKey?: string;
  bonusMenuKey?: string;
  salaryMenuKey?: string;
  benefitMenuKey?: string;
  dependantsMenuKey?: string;
  certificatesMenuKey?: string;
  noteMenuKey?: string;
  handleClickMenu: (keys: any) => void;
  onCloseModal: () => void;
  status?: any;
}

const SideBarMenu = (props: SideBarMenuProps) => {
  const {
    isShowFullMenu,
    isShowHistory = true,
    isShowContract,
    employeeData,
    openKeys,
    employeeMenuKey,
    timesheetMenuKey,
    performanceMenuKey,
    personalMenuKey,
    skillMenuKey,
    historyMenuKey,
    contractMenuKey,
    certificatesMenuKey,
    salaryMenuKey,
    benefitMenuKey,
    noteMenuKey,
    handleClickMenu,
    onCloseModal,
  } = props;
  const { t } = useTranslation(['employee']);
  const dispatch = useAppDispatch();
  const dataPermissions = useAppSelector(selectPermissions);
  const insurance = useAppSelector(selectInsurance);
  const history = useHistory();

  const handleTimesheetClick = () => {
    try {
      // Navigate to the specific employee's timesheet page in new window
      if (!employeeData) {
        throw new Error('Không tìm thấy thông tin nhân viên');
      }

      const employeeId = employeeData.employeeId || employeeData.id;
      const employeeName = employeeData.fullName || employeeData.firstName;

      let timesheetUrl = '';

      if (employeeId) {
        timesheetUrl = `/manage/employee_timesheet/?search=${employeeId}`;
        console.log('⚡ Opening timesheet in new window for employee:', employeeId);
      } else if (employeeName) {
        timesheetUrl = `/manage/employee_timesheet/?search=${employeeName}`;
        console.log('⚡ Opening timesheet in new window for employee:', employeeName);
      } else {
        throw new Error('Không tìm thấy ID hoặc tên nhân viên');
      }

      // Open in new window/tab
      window.open(timesheetUrl, '_blank');

      // Close the employee modal

      return;
    } catch (error) {
      console.error('Lỗi khi mở trang timesheet:', error);
      // Fallback to general timesheet page in new window
      window.open('/manage/employee_timesheet', '_blank');
      // Close the employee modal if needed
    }
  };

  const handlePerformanceClick = async () => {
    // Prevent multiple clicks
    if (isOpeningPerformance) {
      console.log('⚠️ Performance page already opening, ignoring click');
      return;
    }

    setIsOpeningPerformance(true);

    try {
      // Open performance page in new window for the specific employee
      if (!employeeData) {
        throw new Error('Không tìm thấy thông tin nhân viên');
      }

      const employeeId = employeeData.id || employeeData.employeeId;

      if (employeeId) {
        console.log('⚡ Opening performance page in new window for employee:', employeeId);

        // Open performance page in new window/tab
        const performanceUrl = `/performance/employee-pr-management/${employeeId}`;
        window.open(performanceUrl, '_blank');

        // Close the employee modal

        return;
      } else {
        throw new Error('Không tìm thấy ID nhân viên');
      }
    } catch (error) {
      console.error('Lỗi khi mở trang performance:', error);
      // Fallback to general performance page in new window
      window.open('/performance/performance-review', '_blank');
      // Close the employee modal if needed
    } finally {
      // Reset loading state after a short delay
      setTimeout(() => {
        setIsOpeningPerformance(false);
      }, 1000);
    }
  };

  const handleNoteClick = () => {
    // Prevent multiple clicks
    if (isNavigatingToNote) {
      console.log('🔒 Already opening Notes modal, please wait...');
      return;
    }

    try {
      setIsNavigatingToNote(true);

      // Open Notes modal for the employee
      if (!employeeData) {
        throw new Error('Không tìm thấy thông tin nhân viên');
      }

      const employeeId = employeeData.id || employeeData.employeeId;

      if (employeeId) {
        console.log('📝 Opening Notes modal for employee:', employeeId);

        // Open Notes modal
        setIsNotesModalVisible(true);
        return;
      } else {
        throw new Error('Không tìm thấy ID nhân viên');
      }
    } catch (error) {
      console.error('Lỗi khi mở Notes modal:', error);
    } finally {
      // Reset state after a short delay to prevent rapid clicking
      setTimeout(() => setIsNavigatingToNote(false), 500);
    }
  };

  const handleSalaryClick = () => {
    // Prevent multiple clicks
    if (isCheckingSalary) {
      console.log('🔒 Already opening salary page, please wait...');
      return;
    }

    try {
      setIsCheckingSalary(true);

      if (!employeeData) {
        throw new Error('Không tìm thấy thông tin nhân viên');
      }

      const employeeId = employeeData.id || employeeData.employeeId;
      const employeeName = employeeData.fullName || employeeData.firstName;
      const searchValue = employeeData.email || employeeId || employeeName;

      if (searchValue) {
        console.log('⚡ Opening salary page in new window:', searchValue);

        // ⚡ OPTIMIZED: Set localStorage for the new window
        localStorage.setItem('payroll_search_value', searchValue);

        // Dispatch custom event for any listeners
        window.dispatchEvent(
          new CustomEvent('payroll-search', {
            detail: { searchValue },
          }),
        );

        // Open salary page in new window/tab
        const salaryUrl = `/payroll/overall/?search=${encodeURIComponent(searchValue)}`;
        window.open(salaryUrl, '_blank');

        // Close modal

        return;
      } else {
        throw new Error('Không tìm thấy thông tin nhân viên');
      }
    } catch (error) {
      console.error('Lỗi khi mở trang salary:', error);
      // Fallback to general salary page in new window
      window.open('/payroll/overall', '_blank');
    } finally {
      // Reset state after a short delay to prevent rapid clicking
      setTimeout(() => setIsCheckingSalary(false), 500);
    }
  };

  const handleBenefitClick = async () => {
    // Prevent multiple clicks
    if (isCheckingInsurance) {
      console.log('🔒 Already checking insurance, please wait...');
      return;
    }

    try {
      setIsCheckingInsurance(true);

      if (!employeeData) {
        throw new Error('Không tìm thấy thông tin nhân viên');
      }

      const employeeId = employeeData.employeeId || employeeData.id;
      const employeeName = employeeData.fullName || employeeData.firstName;

      if (!employeeId && !employeeName) {
        throw new Error('Không tìm thấy thông tin nhân viên');
      }

      const searchValue = employeeId || employeeName;
      console.log('🎯 Ultra-Fast Navigation:', { employeeId });

      // 🚀 STRATEGY 1: Check existing Redux state first
      const existingIncreaseData = (insurance as any)?.dataSchedulePremium?.data?.result || [];
      const existingDecreaseData = (insurance as any)?.dataDecrease?.data?.result || [];

      if (existingIncreaseData.length > 0 || existingDecreaseData.length > 0) {
        console.log('⚡ Using cached data from Redux store');

        // Quick check in existing data
        const hasIncreaseInsurance = existingIncreaseData.some(
          (record: any) => record?.user?.employeeId === employeeId,
        );

        if (hasIncreaseInsurance) {
          console.log(
            '✅ Found in cached INCREASE data - Opening in new window with instant search',
          );
          setIsCheckingInsurance(false);

          // Use URL parameters for instant search
          const increaseUrl = `/insurance/complusory-insurance?tab=increase&search=${encodeURIComponent(
            searchValue,
          )}&autoSearch=true&employeeId=${encodeURIComponent(employeeId)}`;
          window.open(increaseUrl, '_blank');

          return;
        }

        const hasDecreaseInsurance = existingDecreaseData.some(
          (record: any) => record?.user?.employeeId === employeeId,
        );

        if (hasDecreaseInsurance) {
          console.log(
            '✅ Found in cached DECREASE data - Opening in new window with instant search',
          );
          setIsCheckingInsurance(false);

          // Use URL parameters for instant search
          const decreaseUrl = `/insurance/complusory-insurance?tab=decrease&search=${encodeURIComponent(
            searchValue,
          )}&autoSearch=true&employeeId=${encodeURIComponent(employeeId)}`;
          window.open(decreaseUrl, '_blank');

          return;
        }
      }

      // 🚀 STRATEGY 2: Ultra-fast parallel API calls with minimal data
      console.log('🔍 Fast API check...');

      const [increaseResult, decreaseResult] = await Promise.all([
        dispatch(
          getSchedulePremium({
            fromDate: '',
            toDate: '',
            search: 'increase',
            name: '',
            limit: 10, // Even smaller limit - just need to find employee
            offset: 1,
          }),
        ),
        dispatch(
          getScheduleDecrease({
            fromDate: '',
            toDate: '',
            search: 'decrease',
            name: '',
            limit: 10, // Even smaller limit
            offset: 1,
          }),
        ),
      ]);

      // Quick ID-only matching
      const increaseData = increaseResult.payload?.data?.result || [];
      const decreaseData = decreaseResult.payload?.data?.result || [];

      // Check both in single loop for efficiency
      const hasIncrease = increaseData.some((r: any) => r?.user?.employeeId === employeeId);
      const hasDecrease = decreaseData.some((r: any) => r?.user?.employeeId === employeeId);

      if (hasIncrease) {
        console.log('✅ INCREASE found - Opening in new window with instant search');
        setIsCheckingInsurance(false);

        // Use URL parameters for instant search
        const increaseUrl = `/insurance/complusory-insurance?tab=increase&search=${encodeURIComponent(
          searchValue,
        )}&autoSearch=true&employeeId=${encodeURIComponent(employeeId)}`;
        window.open(increaseUrl, '_blank');

        return;
      }

      if (hasDecrease) {
        console.log('✅ DECREASE found - Opening in new window with instant search');
        setIsCheckingInsurance(false);

        // Use URL parameters for instant search
        const decreaseUrl = `/insurance/complusory-insurance?tab=decrease&search=${encodeURIComponent(
          searchValue,
        )}&autoSearch=true&employeeId=${encodeURIComponent(employeeId)}`;
        window.open(decreaseUrl, '_blank');

        return;
      }

      // Show notification for no insurance
      console.log('❌ No insurance found');
      notificationToast(
        Notification.Type.Error,
        t('employee:employee_manage.no_insurance_modal_title'),
        Notification.Duration._3s,
      );
    } catch (error) {
      console.error('Lỗi khi điều hướng đến trang benefit:', error);

      // Fallback - show notification if there's any error
      notificationToast(
        Notification.Type.Error,
        t('employee:employee_manage.no_insurance_modal_title'),
        Notification.Duration._3s,
      );
    } finally {
      setIsCheckingInsurance(false);
    }
  };

  const [isVisibleUpdateId, setIsVisibleUpdateId] = useState(false);
  const [isAllowEditEmployeeId, setIsAllowEditEmployeeId] = useState(false);
  const [isCheckingInsurance, setIsCheckingInsurance] = useState(false);
  const [isCheckingSalary, setIsCheckingSalary] = useState(false);
  const [isNavigatingToNote, setIsNavigatingToNote] = useState(false);
  const [isOpeningPerformance, setIsOpeningPerformance] = useState(false);
  const [isNotesModalVisible, setIsNotesModalVisible] = useState(false);

  useEffect(() => {
    if (dataPermissions && dataPermissions.includes(PERMISSION_PERSONNEL_RECORD_APPROVAL)) {
      setIsAllowEditEmployeeId(true);
    }
  }, [dataPermissions]);

  let renderAvatar;
  if (employeeData) {
    renderAvatar = (
      <>
        <AvatarBig
          size={87}
          fontSize={'28px'}
          firstName={employeeData.firstName}
          lastName={employeeData.lastName}
        />
        <p>
          {t('employee:employee_manage.employee_id')} : {employeeData.employeeId}
          {isAllowEditEmployeeId && isShowFullMenu && (
            <Icon
              component={() => <EditTwoTone style={{ fontSize: 15, paddingLeft: 10 }} />}
              onClick={() => {
                setIsVisibleUpdateId(true);
              }}
            />
          )}
        </p>
      </>
    );
  } else {
    renderAvatar = (
      <>
        <EmployeeAvatar size={74} icon={<UserOutlined />} />
        <h2>{t('employee:employee_manage.unknown')}</h2>
        <p>
          {t('employee:employee_manage.employee_id')} : {t('employee:employee_manage.unknown')}
        </p>
      </>
    );
  }

  return (
    <>
      <StyledEmployeeAddModalAvatar>{renderAvatar}</StyledEmployeeAddModalAvatar>
      <Menu
        mode="inline"
        selectedKeys={[openKeys]}
        style={{ border: 'none', width: 'max-content' }}
      >
        {isShowFullMenu && (
          <Menu.Item
            key={employeeMenuKey}
            icon={<IdcardOutlined />}
            onClick={handleClickMenu}
            style={{ border: 'none' }}
          >
            {t('employee:employee_manage.menu_employee_info')}
          </Menu.Item>
        )}
        {isShowFullMenu && (
          <Menu.Item
            key={personalMenuKey}
            icon={<UserOutlined />}
            onClick={handleClickMenu}
            style={{ border: 'none' }}
          >
            {t('employee:employee_manage.menu_personnel_info')}
          </Menu.Item>
        )}
        <Menu.Item
          key={skillMenuKey}
          icon={<StarOutlined />}
          onClick={handleClickMenu}
          style={{ border: 'none' }}
        >
          {t('employee:employee_manage.skill_info')}
        </Menu.Item>
        {isShowFullMenu && isShowContract && (
          <Menu.Item
            key={contractMenuKey}
            icon={<SolutionOutlined />}
            onClick={handleClickMenu}
            style={{ border: 'none' }}
          >
            Contract
          </Menu.Item>
        )}
        {isShowFullMenu && (
          <Menu.Item
            key={timesheetMenuKey}
            icon={<ClockCircleOutlined />}
            onClick={handleTimesheetClick}
            style={{ border: 'none' }}
          >
            {t('employee:employee_manage.menu_timesheet')}
          </Menu.Item>
        )}
        {isShowFullMenu && (
          <Menu.Item
            key={performanceMenuKey}
            icon={<TrophyOutlined />}
            onClick={handlePerformanceClick}
            style={{
              border: 'none',
              opacity: isOpeningPerformance ? 0.6 : 1,
              pointerEvents: isOpeningPerformance ? 'none' : 'auto',
            }}
          >
            {isOpeningPerformance
              ? t('employee:employee_manage.loading_opening_performance')
              : t('employee:employee_manage.menu_performance')}
          </Menu.Item>
        )}
        {/* <Menu.Item
          key={certificatesMenuKey}
          icon={<StarOutlined />}
          onClick={handleClickMenu}
          style={{ border: 'none' }}
        >
          {'Certifications'}
        </Menu.Item> */}
        {/* <Menu.Item
          key={allowanceMenuKey}
          icon={<MoneyCollectOutlined />}
          onClick={handleClickMenu}
          style={{ border: 'none' }}
        >
          {t('employee:employee_manage.menu_allowance_info')}
        </Menu.Item>
        <Menu.Item
          key={bonusMenuKey}
          icon={<MoneyCollectOutlined />}
          onClick={handleClickMenu}
          style={{ border: 'none' }}
        >
          {t('employee:employee_manage.menu_bonus_info')}
        </Menu.Item>
        <Menu.Item
          key={salaryMenuKey}
          icon={<MoneyCollectOutlined />}
          onClick={handleClickMenu}
          style={{ border: 'none' }}
        >
          {t('employee:employee_manage.menu_salary_info')}
        </Menu.Item>
        <Menu.Item
          key={dependantsMenuKey}
          icon={<MoneyCollectOutlined />}
          onClick={handleClickMenu}
          style={{ border: 'none' }}
        >
          {t('employee:employee_manage.menu_dependants_info')}
        </Menu.Item> */}
        {isShowFullMenu && (
          <Menu.Item
            key={salaryMenuKey}
            icon={<DollarOutlined />}
            onClick={handleSalaryClick}
            style={{
              border: 'none',
              opacity: isCheckingSalary ? 0.6 : 1,
              pointerEvents: isCheckingSalary ? 'none' : 'auto',
            }}
          >
            {isCheckingSalary
              ? t('employee:employee_manage.loading_general')
              : t('employee:employee_manage.menu_salary_info') || 'Salary'}
          </Menu.Item>
        )}
        {isShowFullMenu && (
          <Menu.Item
            key={benefitMenuKey}
            icon={<MedicineBoxOutlined />}
            onClick={handleBenefitClick}
            style={{
              border: 'none',
              opacity: isCheckingInsurance ? 0.6 : 1,
              pointerEvents: isCheckingInsurance ? 'none' : 'auto',
            }}
          >
            {isCheckingInsurance
              ? t('employee:employee_manage.loading_checking')
              : t('employee:employee_manage.menu_benefit_info') || 'Benefit'}
          </Menu.Item>
        )}
        {isShowFullMenu && isShowHistory && (
          <Menu.Item
            key={historyMenuKey}
            icon={<HistoryOutlined />}
            onClick={handleClickMenu}
            style={{ border: 'none' }}
          >
            {t('employee:employee_manage.record_history')}
          </Menu.Item>
        )}
        {isShowFullMenu && (
          <Menu.Item
            key={noteMenuKey}
            icon={<FileTextOutlined />}
            onClick={handleNoteClick}
            style={{
              border: 'none',
              opacity: isNavigatingToNote ? 0.6 : 1,
              pointerEvents: isNavigatingToNote ? 'none' : 'auto',
            }}
          >
            {isNavigatingToNote
              ? t('employee:employee_manage.loading_general')
              : t('employee:employee_manage.menu_note')}
          </Menu.Item>
        )}
      </Menu>
      <EmployeeUpdateId
        recordId={employeeData?.id}
        employeeIdOld={employeeData?.employeeId}
        isVisible={isVisibleUpdateId}
        onClose={() => {
          setIsVisibleUpdateId(false);
          onCloseModal();
        }}
      />

      {/* Notes Modal */}
      <Note
        visible={isNotesModalVisible}
        onClose={() => setIsNotesModalVisible(false)}
        userId={employeeData?.id || employeeData?.employeeId || ''}
        contextType="PERFORMANCE_REVIEW"
      />
    </>
  );
};

export default SideBarMenu;
